import React, {useContext, useRef} from 'react';
import {IconButton} from '@bonprix/design-system';
import {TrackingContext, TrackingContextAppend} from '../TrackingContext.jsx';
import classNames from 'classnames';
import {ProductSummary} from '../ProductSummary.jsx';
import {CfEntry} from '../CfPreviewContext.jsx';
import {Slider} from '../slider.js';
import {TextTeaser} from './TextTeaser.jsx';
import {ErrorTeaser} from './ErrorTeaser.jsx';
import {FocusTeaser} from './FocusTeaser.jsx';
import {DuplexTeaser} from './DuplexTeaser.jsx';
import {LongVideo} from './LongVideo.jsx';
import {TrinityEvents} from '../../modules/teaser/trinity.js';
import {XRay} from '@teaser/scripts/components/XRay.jsx';

/**
 * @typedef {Object} PriceModel
 * @property {string} priceWholeNumber
 * @property {string} priceFractionalPart
 * @property {string} formattedPrice
 * @property {string | null} formerPriceWholeNumber
 * @property {string | null} formerPriceFractionalPart
 * @property {string | null} formattedFormerPrice
 * @property {boolean} uniquePrice
 * @property {number | null} priceReduction
 */

/**
 * @typedef {Object} ProductSummaryModel
 * @property {string} entryId
 * @property {string} imageUrl
 * @property {string} linkUrlBasePath
 * @property {string} linkUrlQueryParameter
 * @property {string} productName
 * @property {PriceModel} price
 * @property {Array} benefits
 * @property {boolean} soldOut
 */

/**
 * @typedef {Object} ProductSliderModel
 * @property {string} entryId
 * @property {string} backgroundColor
 * @property {string} contentType
 * @property {(DuplexTeaserModel | FocusTeaserModel | TextTeaserModel | ErrorTeaserModel | null)} upperTeaser
 * @property {'PRODUCT_SLIDER'} type
 * @property {boolean} showProductInfo
 * @property {ProductSummaryModel[]} products
 */

/**
 * @param {ProductSliderModel} model
 * @param {Device} device
 * @returns {JSX.Element}
 */
export function ProductSlider({model, device}) {
    const slider = useRef(new Slider());
    const trackingContext = useContext(TrackingContext);

    const handleClick = (product) => {
        if (model.upperTeaser?.type === 'LONG_VIDEO') {
            TrinityEvents.sendProductSliderClick({
                itemId: product.entryId,
                carouselName: 'video_carousel',
                digistyleId: `${product.digistyleId}`,
                functionId: trackingContext.functionId,
                page: trackingContext.page,
                object: 'link',
            });
        }
    };

    return (
        <CfEntry entryId={model.entryId} contentType={model.contentType}>
            <div className="content-platform__product-slider -aop" style={{backgroundColor: model.backgroundColor}}>
                <XRay fieldId="internalName"/>
                <TrackingContextAppend value={{id: model.entryId}}>
                    {model.upperTeaser && (
                        <UpperTeaser key={model.upperTeaser.entryId} model={model.upperTeaser} device={device}/>
                    )}

                    <div className="content-platform__product-slider__slider-container">
                        <div
                            className={classNames(
                                'content-platform__product-slider__slider',
                                model.products.length < 3 && '-static',
                                model.products.length < 4 && '-static-medium',
                                model.products.length < 6 && '-static-large',
                            )}
                            ref={(element) => slider.current.setElement(element)}
                        >
                            {model.products.map((product) => {
                                return (
                                    <TrackingContextAppend value={{itemId: product.entryId}}>
                                        <div
                                            key={product.entryId}
                                            className="content-platform__product-slider__slider__slide"
                                            onClick={() => handleClick(product)}
                                        >
                                            <ProductSummary
                                                product={product}
                                                showProductInfo={model.showProductInfo}
                                                wishListHeartClass={
                                                    'content-platform__add-to-wishlist-heart__product-slider'
                                                }
                                            ></ProductSummary>
                                        </div>
                                    </TrackingContextAppend>
                                );
                            })}
                        </div>

                        <div className="content-platform__product-slider__button -prev">
                            <IconButton
                                icon="arrow-left"
                                type="button"
                                aria-label="Previous Slide"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    slider.current.slideLeft(5);
                                }}
                            />
                        </div>
                        <div className="content-platform__product-slider__button -next">
                            <IconButton
                                icon="arrow-right"
                                type="button"
                                aria-label="Next Slide"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    slider.current.slideRight(5);
                                }}
                            />
                        </div>
                    </div>
                </TrackingContextAppend>
            </div>
        </CfEntry>
    );
}

/**
 * @param {(DuplexTeaserModel | FocusTeaserModel | TextTeaserModel | LongVideoModel | ErrorTeaserModel)} model
 * @param {Device} device
 * @returns {JSX.Element}
 */

function UpperTeaser({model, device}) {
    switch (model.type) {
        case 'DUPLEX_TEASER':
            return <DuplexTeaser model={model} device={device}/>;
        case 'FOCUS_TEASER':
            return <FocusTeaser model={model} device={device} style={{display: 'block'}}/>;
        case 'TEXT_TEASER':
            return <TextTeaser model={model} device={device}/>;
        case 'LONG_VIDEO':
            return <LongVideo model={model} device={device}/>;
        case 'ERROR_TEASER':
            return <ErrorTeaser model={model} device={device}/>;

        default:
            return null;
    }
}
